import { Mic, Square } from 'lucide-react'
import { ComposerVoiceIcon } from '@/components/ComposerVoiceIcon'
import s from './scribe.module.css'

export type ScribeTrigger = 'aria' | 'microphone' | 'glyph'

interface Props {
  live: boolean
  disabled?: boolean
  onClick: () => void
  /** md = the standalone 44px orb; sm = a 30px orb for label rows. */
  size?: 'sm' | 'md'
  /**
   * `aria` and `microphone` both render the design system's Scribe orb (the
   * form modal's close-button surface carrying the plain clay mic); both
   * names stay for API compatibility. `glyph` is the queue decision pane's
   * bare 36px Lucide mic.
   */
  trigger?: ScribeTrigger
  /** Names the orb in place; the word unfurls left on hover/focus. Ignored by `glyph`. */
  hoverLabel?: string
}

/**
 * The Scribe trigger (design system → Components → Composer & Scribe): a 44px
 * orb on the form modal's close-button surface showing the clay microphone
 * lifted out of its medallion — navy on light, cream on dark — and the stop
 * square while recording. Listening retires the orange border and the pulse
 * rings for the worm ring: an orange arc that laps the button once every
 * 2.2s (a static 270° arc under reduced motion).
 */
export function ScribeButton({
  live,
  disabled,
  onClick,
  size = 'md',
  trigger = 'aria',
  hoverLabel: hoverLabelProp,
}: Props) {
  const glyph = trigger === 'glyph'
  // The bare glyph never unfurls a label.
  const hoverLabel = glyph ? undefined : hoverLabelProp
  const classes = [
    glyph ? s.glyphBtn : s.orbBtn,
    !glyph && size === 'sm' ? s.orbBtnSm : '',
    live ? s.orbBtnLive : '',
  ]
    .filter(Boolean)
    .join(' ')
  const label = live ? 'Stop dictating' : 'Dictate with Scribe'
  const displayLabel = live ? 'Stop' : hoverLabel

  return (
    <button
      type="button"
      className={classes}
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      aria-pressed={live}
      data-labeled={hoverLabel ? true : undefined}
      title={hoverLabel ? undefined : label}
    >
      {hoverLabel && (
        <span className={s.label} aria-hidden="true">
          <span className={s.labelText}>{displayLabel}</span>
        </span>
      )}
      {glyph ? (
        live ? (
          <Square size={14} strokeWidth={0} fill="currentColor" className={s.glyphIcon} aria-hidden />
        ) : (
          <Mic size={20} className={s.glyphIcon} aria-hidden />
        )
      ) : (
        <span className={s.iconSlot}>
          <span className={s.glyphArt}>
            <ComposerVoiceIcon mode={live ? 'stop' : 'mic'} variant="plain" />
          </span>
        </span>
      )}
      {/* The worm ring: a 64px circle (45px at the small scale) centered on the
          glyph, 10px past the orb's edge; painted only while live. */}
      <svg className={s.scribeWorm} viewBox="0 0 64 64" aria-hidden="true">
        <circle cx="32" cy="32" r="28" pathLength="100" />
      </svg>
    </button>
  )
}
